import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod";
import { isValidYmd } from "@contractorhub/shared";
import {
  adminDashboardQuery,
  directoryDashboardQuery,
  myDashboardQuery,
} from "../../lib/queries";
import { AdminDashboard } from "../../components/dashboard/admin-dashboard";
import { DirectoryDashboard } from "../../components/dashboard/directory-dashboard";
import { ContractorDashboard } from "../../components/dashboard/contractor-dashboard";

/**
 * Everyone's home, one path for every role.
 *
 * Each role fetches only its own dashboard: the loader and the page switch on
 * the signed-in role, so an admin's money is never so much as requested for a
 * user admin or a contractor, and each endpoint is closed to the others anyway.
 * The date range is the admin's alone; the other two answer for "now".
 */

// A reversed range (a hand-typed or old link) is swapped rather than sent: the
// API answers it with a 400, and a 400 in the loader is the error page.
const searchSchema = z
  .object({
    from: z.string().refine(isValidYmd).optional(),
    to: z.string().refine(isValidYmd).optional(),
  })
  .transform((search) =>
    search.from && search.to && search.from > search.to
      ? { from: search.to, to: search.from }
      : search
  );

export const Route = createFileRoute("/_authed/dashboard")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    const { queryClient, user } = context;
    if (user.role === "ADMIN")
      return queryClient.ensureQueryData(adminDashboardQuery(deps));
    if (user.role === "USER_ADMIN")
      return queryClient.ensureQueryData(directoryDashboardQuery);
    return queryClient.ensureQueryData(myDashboardQuery);
  },
  component: DashboardPage,
});

function DashboardPage() {
  const { user } = Route.useRouteContext();
  if (user.role === "ADMIN") return <AdminPage />;
  if (user.role === "USER_ADMIN") return <DirectoryPage />;
  return <ContractorPage />;
}

function AdminPage() {
  const { user } = Route.useRouteContext();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(adminDashboardQuery(search));
  // A new range runs the loader before the page re-renders; until then the old
  // figures stay on screen, and this says they are about to change.
  const refreshing = useRouterState({ select: (s) => s.isLoading });

  return (
    <AdminDashboard
      user={user}
      data={data}
      range={search}
      refreshing={refreshing}
      onRangeChange={(next) => void navigate({ to: "/dashboard", search: next })}
    />
  );
}

function DirectoryPage() {
  const { user } = Route.useRouteContext();
  const { data } = useSuspenseQuery(directoryDashboardQuery);
  return <DirectoryDashboard user={user} data={data} />;
}

function ContractorPage() {
  const { user } = Route.useRouteContext();
  const { data } = useSuspenseQuery(myDashboardQuery);
  return <ContractorDashboard user={user} data={data} />;
}
